import { useQueries } from "@tanstack/react-query";
import { GKSearchSelect, Grid2, Typography } from "components";
import DashboardLayout from "layouts/DashboardLayout";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { updateFilter } from "store/slices/tableSlice";
import { useAppDispatch, useAppSelector } from "store/store";
import axiosInstance from "utils/axiosInstance";
import { QueryKeys } from "utils/queryKey";
import { PortfolioDropdownInterface } from "utils/types";
import useDocumentTitle from "utils/useDocumentTitle";
import InsigntsPageContent from "./InsigntsPageContent";

const HoldingsInsights = () => {
  useDocumentTitle("Holdings Insights");

  const dispatch = useAppDispatch();

  const commonFilter = useAppSelector((state) => state?.table?.commonFilter);

  const [selectedItems, setSelectedItems] = useState<
    PortfolioDropdownInterface[]
  >([]);

  const navigate = useNavigate();

  const [
    { data: portfolioList, isFetching: isPortfolioLoading },
    { data: pieChartData },
  ] = useQueries({
    queries: [
      {
        queryKey: [QueryKeys.PORTFOLIO_OVERVIEW],
        queryFn: () => axiosInstance.get(`/portfolios/dropdown/`),
      },
      {
        queryKey: [selectedItems, QueryKeys.INSIGHTS_HOLDINGS],
        queryFn: () =>
          axiosInstance.post(`/holdings/selected/`, {
            portfolios: selectedItems
              ?.map((data: any) => data?.portfolios)
              ?.flat()
              ?.map((dataItem) => dataItem?.value),
          }),
        keepPreviousData: true,
      },
    ],
  });

  return (
    <DashboardLayout title="Holdings Insights" loading={isPortfolioLoading}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 10,
          marginBottom: 10,
          background: "var(--white)",
          borderRadius: 10,
          border: "solid 1px #25778544",
          padding: "10px",
          position: "sticky",
          top: -8,
          boxShadow: " 0 3px 10px rgb(0 0 0 / 0.2)",
          zIndex: 2,
        }}
      >
        <GKSearchSelect
          multiple
          disableClearable
          style={{
            minWidth: "400px",
            maxWidth: "500px",
          }}
          loading={isPortfolioLoading}
          onChange={(event, val) => {
            setSelectedItems(val);
            dispatch(
              updateFilter({
                name: "insights",
                filter: {
                  skip: 0,
                  page: 0,
                  take: 25,
                  sortModel: [],
                  search: "",
                  showNonZero: commonFilter.insights.showNonZero,
                  customFilter: {},
                },
              }),
            );
          }}
          placeholder="Select ..."
          value={selectedItems}
          groupBy={(option: any) => option.type?.toUpperCase()}
          renderOption={(props, option) => {
            return (
              <li
                {...props}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                }}
              >
                <Typography fontWeight={600}>{option.clientName}</Typography>
                <Typography>{option.name}</Typography>
              </li>
            );
          }}
          options={
            portfolioList?.data?.map((item: any) => ({
              name: `${item?.name} - ${item?.code}`,
              value: item?.id,
              type: item?.type,
              clientName: item?.clientName,
              portfolios: item?.portfolios?.map((portfolio: any) => ({
                label: portfolio?.name,
                value: portfolio?.id,
                portfolioId: portfolio?.portfolioId,
              })),
            })) || []
          }
          getOptionLabel={(option) => option.name || option}
        />
      </div>
      <Grid2 container spacing={1} maxHeight={`calc(100vh - 154px)`}>
        <Grid2 size={12}>
          <InsigntsPageContent
            allHoldingsInsightsData={pieChartData?.data || []}
            isLoading={isPortfolioLoading}
            onRowClick={(row) => {
              navigate(`/app/insights-holdings/${row?.row?.gcode}/details`, {
                state: {
                  portfoliosIds: selectedItems
                    ?.map((data: any) => data?.portfolios)
                    ?.flat()
                    ?.map((dataItem) => dataItem?.value),
                },
              });
            }}
            selectedItems={selectedItems}
          />
        </Grid2>
      </Grid2>
    </DashboardLayout>
  );
};

export default HoldingsInsights;
